import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { NullableSelectOption } from '@visalex/forms';
import {
  EmptyPage,
  FaqHero,
  FAQPage,
  getOptions,
  getPathArray,
  PostsFaqContent,
  PostsList,
} from '@visalex/help-center';

import cx from 'clsx';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { returnPosts } from '../api/posts/[...slug]';

import styles from '@/components/Help/FAQ.module.scss';

const FAQ: FunctionComponent<PostsFaqContent> = ({ posts, sections, listOfAllPosts, listOfFaq }) => {
  const [postsList, setPostsList] = useState(posts);
  const [sectionsList, setSectionsList] = useState(sections);
  const { t } = useTranslation();
  const router = useRouter();

  const [currentPath, setCurrentPath] = useState('');

  /**
   * Handler on post search. Displays found post or resets search
   * @param newValue
   */
  const handleSearchOptionChange = (newValue: NullableSelectOption) => {
    // reset search on empty value
    if (!newValue) {
      setPostsList(posts);
      setSectionsList(sections);

      return;
    }

    // get found post which is chosen in dropdown
    const foundPost = listOfAllPosts.find((post) => post.path === newValue.value);

    if (foundPost) {
      setPostsList([
        {
          ...foundPost.post,
          url: foundPost.path.substring(1),
        },
      ]);
      setSectionsList([]);
    }
  };

  useEffect(() => {
    setSectionsList(sections);
    setPostsList(posts);
  }, [posts, sections]);

  /**
   * Get array of breadcrumbs based on current path
   */
  const breadcrumbs = useMemo(() => {
    if (router) {
      const linkPath = router.asPath.split('/');

      return getPathArray(linkPath);
    }

    return [];
  }, [router]);

  /**
   * Handler on post click. Redirects to a new route which is specified inside the post
   * @param e - event
   * @param url - post's url
   */
  const handlePostClick = (e: React.MouseEvent<HTMLDivElement>, url: string) => {
    e.preventDefault();
    router.push(`${router.asPath}/${url}`);
  };

  useEffect(() => {
    if (router) {
      const linkPath = router.asPath.replace('/help', '');

      setCurrentPath(linkPath);
    }
  }, [router]);

  const pageContent = <PostsList sectionsList={sectionsList} postsList={postsList} handleClick={handlePostClick} />;

  return (
    <Layout className={styles.faq} title={t('faq.seo-title')} description={t('faq.seo-description')}>
      <div id="faq-root" />
      <FaqHero
        options={getOptions(listOfAllPosts)}
        handleSearchOptionChange={handleSearchOptionChange}
        title={t('faq.hero.title')}
        searchPlaceholder={t('faq.hero.search')}
      />
      <div className={cx(styles.faq__article, styles['faq__article--main'])}>
        {listOfFaq && listOfFaq.length > 0 ? (
          <FAQPage listOfFaq={listOfFaq} breadcrumbs={breadcrumbs} currentPath={currentPath}>
            {pageContent}
          </FAQPage>
        ) : (
          <EmptyPage />
        )}
      </div>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  /**
   * Getting all posts, sections, faq list
   */
  const { posts, sections, listOfAllPosts, listOfFaq } = await returnPosts(['marketing', 'en']);

  return {
    props: { posts, sections, listOfAllPosts, listOfFaq, ...(await serverSideTranslations(locale as string)) },
  };
};

export default FAQ;
